import SmoothScrollbars from '@rio-cloud/rio-uikit/components/smoothScrollbars/SmoothScrollbars';
import Fade from '@rio-cloud/rio-uikit/Fade';
import Button from '@rio-cloud/rio-uikit/Button';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { TOUR_HANDLING_DETAILS, TOUR_HANDLING_ROUTE } from '../../common/constants';
import { PageNavigationBar } from '../../components/PageNavigationBar/PageNavigationBar';
import { EditConfirmationDialog } from '../../components/EditConfirmationDialog/EditConfirmationDialog';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { SectionsContainer } from '../../containers/SectionsContainer/SectionsContainer';
import { TourSummary } from '../../containers/TourSummary/TourSummary';
import { propagateRoute } from '../../features/livemonitorCommunication/MapRouteRendering';
import { updateItineraryWaypoints } from '../../features/search/updateItineraryWaypoint';
import { editTour } from '../../features/tourEdit/editTour';
import {
    selectDraftExistsFlag,
    selectPageDisplayed,
    selectPastTourFlag,
    selectTourHandlingId,
} from '../../store/app/appSelectors';
import { updatePage, updatePastTourFlag } from '../../store/app/appSlice';
import { useGetTourQuery, useFetchTourItinerariesQuery } from '../../store/facade/facadeApiV3';
import { selectItineraries, selectSelectedItinerary } from '../../store/tour/tourSelectors';

export const TourDetailsManagerPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const showContent = useAppSelector(selectPageDisplayed) === TOUR_HANDLING_DETAILS;
    const tourId = useAppSelector(selectTourHandlingId);
    useFetchTourItinerariesQuery({ id: tourId ?? '' }, { skip: !tourId });
    const { isSuccess: isTourFulfilled } = useGetTourQuery({ id: tourId ?? '' }, { skip: !tourId });
    const storeItineraries = useAppSelector(selectItineraries);
    const itineraries = storeItineraries;
    const selectedItinerary = useAppSelector(selectSelectedItinerary);
    const selectedRouteData = itineraries?.at(selectedItinerary ?? 0);

    const pastTourFlag = useAppSelector(selectPastTourFlag);
    const draftExistsFlag = useAppSelector(selectDraftExistsFlag);
    const canEdit = !!tourId && !pastTourFlag && isTourFulfilled;

    const [showEditConfirmation, setShowEditConfirmation] = useState(false);

    // Keep a ref to the latest page value so handleNavigationEnd always reads the
    // up-to-date value even after the Fade exit animation completes (Requirement 5.6)
    const currentPage = useAppSelector(selectPageDisplayed);
    const currentPageRef = useRef(currentPage);
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        if (storeItineraries && storeItineraries.length > 0) {
            // Only update route settings waypoints when this is NOT a past tour (Requirement: past tour
            // selection must not modify the route settings fields)
            if (!pastTourFlag) {
                dispatch(updateItineraryWaypoints);
            }
            dispatch(propagateRoute(storeItineraries, selectedItinerary ?? 0));
        }
    }, [storeItineraries, dispatch, selectedItinerary, pastTourFlag]);

    const handleNavigationEnd = () => {
        navigate(currentPageRef.current, { replace: false });
    };

    const handleNavigationStart = () => {
        dispatch(updatePage(TOUR_HANDLING_ROUTE));
        dispatch(updatePastTourFlag(true)); // Requirement 2.10
    };

    const handleEditClick = () => {
        if (draftExistsFlag) {
            setShowEditConfirmation(true); // no dispatch — Requirement 7.1
            return;
        }
        dispatch(editTour); // Requirement 5
    };

    const handleConfirmEdit = () => {
        dispatch(editTour); // Requirement 7.4
        setShowEditConfirmation(false);
    };

    const handleCancelEdit = () => {
        setShowEditConfirmation(false); // Requirement 7.5-7.7 — no other dispatch
    };

    return (
        <Fade initial animationStyle={Fade.PAGE_BACK} show={showContent} onExitComplete={handleNavigationEnd}>
            <div className='display-flex flex-column height-100vh' data-testid='manager-summary-page'>
                <div data-testid='summary-back-button'>
                    <PageNavigationBar title='intl-msg:smartRoute.details.title' navBack={handleNavigationStart} />
                </div>
                <EditConfirmationDialog
                    show={showEditConfirmation}
                    onConfirm={handleConfirmEdit}
                    onCancel={handleCancelEdit}
                />
                <SmoothScrollbars>
                    {itineraries && (
                        <div
                            className='display-flex flex-column gap-25 padding-bottom-15 padding-top-15 padding-x-15'
                            data-testid='tour-handling-layout'
                        >
                            {canEdit && (
                                <Button
                                    data-testid='tour-details-edit-button'
                                    onClick={handleEditClick}
                                    bsStyle={Button.SECONDARY}
                                    bsSize={Button.SM}
                                    iconName='rioglyph-pencil'
                                >
                                    <FormattedMessage id='intl-msg:smartRoute.tour.edit.button' />
                                </Button>
                            )}
                            <div className='display-flex align-items-start justify-content-between'>
                                <div className='flex-1'>
                                    <TourSummary itineraries={itineraries} selectedRoute={selectedItinerary ?? 0} />
                                </div>
                            </div>

                            {selectedRouteData && <SectionsContainer />}
                        </div>
                    )}
                </SmoothScrollbars>
            </div>
        </Fade>
    );
};
